import React, { useMemo, useState } from "react";
import { Download, X } from "lucide-react";
import { formatTime } from "../../../helpers/generalHelpers";
import AgentXYChart from "./charts/AgentXYChart";
import SelectedLinkAgentsTable from "./SelectedLinkAgentsTable";
import { arrivalPercentiles } from "./resultsSummaryData";
import useSelectedLinkSummaryData from "./useSelectedLinkSummaryData";
import { downloadSummaryCsv } from "./resultsSummaryDownload";
import useBuilderRunSummary from "./useBuilderRunSummary";
import { normalizeExitRows, normalizeTimelinePoints } from "./api/builderResultsApi";
import "./MBResultsSummary.css";

const CURVE_CONFIG = {
  type: "multiline",
  categoryKey: "label",
  categoryLabel: "Simulation time",
  valueLabel: "Vehicles",
  series: [
    { key: "arrived", label: "Total arrived", color: "#38bdf8" },
    { key: "queued", label: "Queued", color: "#f59e0b" },
    { key: "running", label: "Running", color: "#a78bfa" },
  ],
};

const LINK_CURVE_CONFIG = {
  type: "multiline",
  categoryKey: "label",
  categoryLabel: "Simulation time",
  valueLabel: "Vehicles on selected links",
  series: [
    { key: "queued", label: "Queued", color: "#f59e0b" },
    { key: "running", label: "Running", color: "#a78bfa" },
  ],
};

const LINK_TRAVEL_TIME_CONFIG = {
  type: "multiline",
  categoryKey: "label",
  categoryLabel: "Simulation time",
  valueLabel: "Travel time",
  valueUnit: "min",
  preserveGaps: true,
  series: [
    { key: "avg", label: "Average", color: "#38bdf8" },
    { key: "min", label: "Minimum", color: "#34d399" },
    { key: "max", label: "Maximum", color: "#f59e0b" },
  ],
};

function SelectionRow({ label, ids, onRemove }) {
  if (!ids.size) return null;
  return <div className="mb-results-summarySelection">
    <span className="mb-results-summarySelectionLabel">{label}</span>
    <div className="mb-results-summaryPills">
      {[...ids].map((id) => <span className="mb-results-summaryPill" key={id}>
        <span title={id}>{id}</span>
        <button type="button" onClick={() => onRemove?.(id)} aria-label={`Remove ${label.toLowerCase()} ${id}`}><X size={13} /></button>
      </span>)}
    </div>
  </div>;
}

function ChartDataDownload({ filename, rows }) {
  return <div className="mb-results-summaryDownload">
    <button type="button" aria-label="Download chart data as CSV" title="Download CSV" onClick={() => downloadSummaryCsv(filename, rows)}><Download size={17} /></button>
  </div>;
}

export default function MBResultsSummary({ builderId, run, prefixes, totalTime, selectedTime, isVisible, selectedNodeIds, selectedLinkIds, onToggleNode, onToggleLink }) {
  const [tab, setTab] = useState("percentiles");
  const activeTab = selectedLinkIds.size || !["linkVehicles", "linkTravelTime", "linkAgents"].includes(tab) ? tab : "percentiles";
  const selectedLinks = useMemo(() => [...selectedLinkIds].map(String).sort(), [selectedLinkIds]);
  const summary = useBuilderRunSummary({ builderId, runId: run?.id, isVisible });
  const apiTimeline = useMemo(() => summary.data ? normalizeTimelinePoints(summary.data.timeline) : [], [summary.data]);
  const apiArrivalRows = useMemo(() => {
    let previous = 0;
    return apiTimeline.filter((point) => point.arrived !== null).map((point) => {
      const row = { time: point.time, arrived: Math.max(0, point.arrived - previous), cumulativeArrived: point.arrived };
      previous = point.arrived;
      return row;
    });
  }, [apiTimeline]);
  const { history, historyRows, travelTime, travelTimeRows, agents } = useSelectedLinkSummaryData({
    builderId, runId: run?.id, prefixes, selectedTime, selectedIds: selectedLinks, maxTime: Math.ceil(totalTime),
    loadHistory: activeTab === "linkVehicles", loadTravelTime: activeTab === "linkTravelTime", loadAgents: activeTab === "linkAgents", isVisible,
  });
  const exitCounts = useMemo(() => (normalizeExitRows(summary.data?.exits) || [])
    .sort((a, b) => b.share - a.share || b.count - a.count || a.exitId.localeCompare(b.exitId, undefined, { numeric: true })), [summary.data]);
  const totalExitArrivals = summary.data?.totalAgentsArrived != null && Number.isFinite(Number(summary.data.totalAgentsArrived)) ? Number(summary.data.totalAgentsArrived) : exitCounts.reduce((sum, row) => sum + row.count, 0);
  const percentiles = useMemo(() => arrivalPercentiles(apiArrivalRows), [apiArrivalRows]);
  const chartRows = useMemo(() => apiTimeline.map((row) => ({ ...row, label: formatTime(row.time, { style: "digital" }) })), [apiTimeline]);
  const hasCurveData = chartRows.some((row) => row.arrived !== null || row.queued !== null || row.running !== null);
  const linkChartRows = useMemo(() => historyRows.map((row) => ({ ...row, label: formatTime(row.time, { style: "digital" }) })), [historyRows]);
  const hasLinkHistory = linkChartRows.some((row) => row.queued !== null || row.running !== null);
  const travelTimeChartRows = useMemo(() => travelTimeRows.map((row) => ({
    ...row,
    avg: row.avg == null ? null : row.avg / 60,
    min: row.min == null ? null : row.min / 60,
    max: row.max == null ? null : row.max / 60,
    label: formatTime(row.time, { style: "digital" }),
  })), [travelTimeRows]);
  const hasTravelTime = travelTimeChartRows.some((row) => row.avg !== null || row.min !== null || row.max !== null);

  return <section className="mb-results-summary" aria-label="Simulation summary">
    {(selectedNodeIds.size > 0 || selectedLinkIds.size > 0) && <div className="mb-results-summarySelections">
      <SelectionRow label="Selected nodes" ids={selectedNodeIds} onRemove={onToggleNode} />
      <SelectionRow label="Selected links" ids={selectedLinkIds} onRemove={onToggleLink} />
    </div>}
    <div className="mb-results-summaryTabs" role="tablist" aria-label="Result summaries">
      <button type="button" role="tab" aria-selected={activeTab === "percentiles"} className={activeTab === "percentiles" ? "active" : ""} onClick={() => setTab("percentiles")}>Arrival percentiles</button>
      <button type="button" role="tab" aria-selected={activeTab === "exits"} className={activeTab === "exits" ? "active" : ""} onClick={() => setTab("exits")}>Exit concentration</button>
      <button type="button" role="tab" aria-selected={activeTab === "curves"} className={activeTab === "curves" ? "active" : ""} onClick={() => setTab("curves")}>Arrival Timeline</button>
      {!!selectedLinkIds.size && <button type="button" role="tab" aria-selected={activeTab === "linkVehicles"} className={activeTab === "linkVehicles" ? "active" : ""} onClick={() => setTab("linkVehicles")}>Link(s) timeline</button>}
      {!!selectedLinkIds.size && <button type="button" role="tab" aria-selected={activeTab === "linkTravelTime"} className={activeTab === "linkTravelTime" ? "active" : ""} onClick={() => setTab("linkTravelTime")}>Link(s) travel time</button>}
      {!!selectedLinkIds.size && <button type="button" role="tab" aria-selected={activeTab === "linkAgents"} className={activeTab === "linkAgents" ? "active" : ""} onClick={() => setTab("linkAgents")}>Link(s) agents</button>}
    </div>
    <div className="mb-results-summaryBody" role="tabpanel">
      {!run && <p className="mb-results-summaryMessage">Select a completed run to view its summary.</p>}
      {run && activeTab === "percentiles" && <>
        {summary.loading && <p className="mb-results-summaryMessage">Loading arrivals…</p>}
        {!summary.loading && summary.error && <p className="mb-results-summaryMessage">{summary.error}</p>}
        {!summary.loading && !summary.error && !percentiles.length && <p className="mb-results-summaryMessage">No arrivals are recorded for this run.</p>}
        {!!percentiles.length && <div className="mb-results-summaryDownload"><button type="button" aria-label="Download arrival percentile table as CSV" title="Download CSV" onClick={() => downloadSummaryCsv(`arrival-percentiles-${run.id}.csv`, percentiles.map(({ percentile, time }) => ({ percentile, time_seconds: time })))}><Download size={17} /></button></div>}
        {!!percentiles.length && <div className="mb-results-summaryTableWrap"><table className="mb-results-summaryTable">
          <thead><tr><th scope="col">Arrived</th><th scope="col">Time</th><th scope="col">Seconds</th></tr></thead>
          <tbody>{percentiles.map(({ percentile, time }) => <tr key={percentile}><td>{percentile}%</td><td>{formatTime(Math.round(time), { style: "digital" })}</td><td>{Math.round(time).toLocaleString()}</td></tr>)}</tbody>
        </table></div>}
      </>}
      {run && activeTab === "exits" && <>
        {summary.loading && <p className="mb-results-summaryMessage">Loading exit arrivals…</p>}
        {!summary.loading && summary.error && <p className="mb-results-summaryMessage">{summary.error}</p>}
        {!summary.loading && !summary.error && !exitCounts.length && <p className="mb-results-summaryMessage">No exit arrivals are recorded for this run.</p>}
        {!summary.loading && !summary.error && !!exitCounts.length && <>
          <div className="mb-results-exitHeader"><strong>{totalExitArrivals.toLocaleString()} agents arrived</strong><span>Across {exitCounts.length} exits</span></div>
          <div className="mb-results-summaryDownload"><button type="button" aria-label="Download exit concentration table as CSV" title="Download CSV" onClick={() => downloadSummaryCsv(`exit-concentration-${run.id}.csv`, exitCounts.map(({ exitId, count, share }) => ({ exit_node_id: exitId, agents_arrived: count, share_percent: Number(share.toFixed(1)) })))}><Download size={17} /></button></div>
          <div className="mb-results-summaryTableWrap"><table className="mb-results-summaryTable mb-results-exitTable">
            <thead><tr><th scope="col"><span className="mb-results-srOnly">Select</span></th><th scope="col">Exit node</th><th scope="col">Agents arrived</th><th scope="col">Share</th></tr></thead>
            <tbody>{exitCounts.map(({ exitId, count, share }) => <tr key={exitId}><td><input type="checkbox" checked={selectedNodeIds.has(exitId)} onChange={() => onToggleNode?.(exitId)} aria-label={`Select exit node ${exitId}`} /></td><td>{exitId}</td><td>{count.toLocaleString()}</td><td>{share.toFixed(1)}%</td></tr>)}</tbody>
          </table></div>
        </>}
      </>}
      {run && activeTab === "curves" && <>
        {summary.loading && <p className="mb-results-summaryProgress" role="status">Loading arrival timeline…</p>}
        {!summary.loading && summary.error && <p className="mb-results-summaryMessage">{summary.error}</p>}
        {!summary.loading && !summary.error && !hasCurveData && <p className="mb-results-summaryMessage">Vehicle counts are unavailable for this run.</p>}
        {!summary.loading && !summary.error && hasCurveData && <>
          <ChartDataDownload filename={`arrival-timeline-${run.id}.csv`} rows={apiTimeline.map((row) => ({ time_seconds: row.time, total_arrived: row.arrived, queued: row.queued, running: row.running }))} />
          <AgentXYChart rows={chartRows} config={CURVE_CONFIG} selectedTime={selectedTime} />
        </>}
      </>}
      {run && activeTab === "linkVehicles" && <>
        {history.loading && <p className="mb-results-summaryProgress" role="status">Loading selected links…</p>}
        {!history.loading && history.error && <p className="mb-results-summaryMessage">{history.error}</p>}
        {!history.loading && !history.error && !hasLinkHistory && <p className="mb-results-summaryMessage">Link counts are unavailable for this run.</p>}
        {!history.loading && !history.error && hasLinkHistory && <>
          <ChartDataDownload filename={`selected-links-vehicles-${run.id}.csv`} rows={historyRows.map((row) => ({ link_ids: selectedLinks.join(";"), time_seconds: row.time, queued: row.queued, running: row.running }))} />
          <AgentXYChart rows={linkChartRows} config={LINK_CURVE_CONFIG} selectedTime={selectedTime} />
        </>}
      </>}
      {run && activeTab === "linkTravelTime" && <>
        {travelTime.loading && <p className="mb-results-summaryProgress" role="status">Loading link travel times…</p>}
        {!travelTime.loading && travelTime.error && <p className="mb-results-summaryMessage">{travelTime.error}</p>}
        {!travelTime.loading && !travelTime.error && !hasTravelTime && <p className="mb-results-summaryMessage">Travel times are unavailable for these links.</p>}
        {!travelTime.loading && !travelTime.error && hasTravelTime && <>
          <ChartDataDownload filename={`selected-links-travel-time-${run.id}.csv`} rows={travelTimeRows.map((row) => ({ link_ids: selectedLinks.join(";"), time_seconds: row.time, avg_travel_time_seconds: row.avg, min_travel_time_seconds: row.min, max_travel_time_seconds: row.max }))} />
          <AgentXYChart rows={travelTimeChartRows} config={LINK_TRAVEL_TIME_CONFIG} selectedTime={selectedTime} />
        </>}
      </>}
      {run && activeTab === "linkAgents" && <>
        {agents.loading && <p className="mb-results-summaryMessage">Loading agents at {formatTime(selectedTime, { style: "digital" })}…</p>}
        {!agents.loading && agents.error && <p className="mb-results-summaryMessage">{agents.error}</p>}
        {!agents.loading && !agents.error && <SelectedLinkAgentsTable rows={agents.rows} selectedTime={selectedTime} runId={run.id} />}
      </>}
    </div>
  </section>;
}
